/**
 * Pack Builder - Handles variant selection and flavor building
 */

class PackBuilder {
  constructor() {
    this.container = document.querySelector('[data-pack-builder]');
    if (!this.container) return;

    this.qtyPerPack = parseInt(this.container.dataset.qtyPerPack, 10) || 6;
    this.incompleteText = this.container.dataset.incompleteText || 'ENCORE {count} SAVEUR À SÉLECTIONNER';
    this.unitLabel = this.container.dataset.unitLabel || 'Barz';

    // Load variants data
    const variantsData = this.container.querySelector('[data-pack-builder-variants]');
    this.variants = variantsData ? JSON.parse(variantsData.textContent) : [];

    // Current state
    this.selectedVariant = null;
    this.flavorSelections = new Map();
    this.totalSelected = 0;
    this.pendingUpgrade = null;

    // Cache DOM elements
    this.cacheElements();
    this.init();
  }

  cacheElements() {
    this.variantButtons = this.container.querySelectorAll('.pack-builder__variant');
    this.variantsContainer = this.container.querySelector('.pack-builder__variants');
    this.variantsToggle = this.container.querySelector('[data-variants-toggle]');
    this.variantsToggleText = this.variantsToggle?.querySelector('[data-toggle-text]');
    this.flavorCards = this.container.querySelectorAll('.pack-builder__flavor');
    this.counterCurrent = this.container.querySelector('[data-counter-current]');
    this.counterMax = this.container.querySelector('[data-counter-max]');
    this.priceDisplay = this.container.querySelector('[data-variant-price]');
    this.comparePriceDisplay = this.container.querySelector('[data-variant-compare-price]');
    this.flavorsInput = this.container.querySelector('[data-flavors-input]');

    // Popup elements - outside container for z-index
    this.popup = document.querySelector('.pack-builder__popup[data-upgrade-popup]');
    if (this.popup) {
      this.popupConfirm = this.popup.querySelector('[data-popup-confirm]');
      this.popupCloseButtons = this.popup.querySelectorAll('[data-popup-close]');
      this.currentVariantName = this.popup.querySelector('[data-current-variant-name]');
      this.nextVariantName = this.popup.querySelector('[data-next-variant-name]');
    }

    // Submit button and its child elements
    this.submitButton = document.querySelector('.product-form__submit[data-action="add-to-cart"]');
    if (this.submitButton) {
      this.submitButtonTextWrapper = this.submitButton.querySelector('.fw-6:not([variant-price-change])');
      this.submitButtonSeparator = this.submitButton.querySelector('.submit-separator');
      this.submitButtonPriceWrapper = this.submitButton.querySelector('[variant-price-change]');
      this.submitButtonOriginalTextHTML = this.submitButtonTextWrapper?.innerHTML || '';
    }
  }

  init() {
    this.initSelectedVariant();
    this.bindVariantButtons();
    this.bindVariantsToggle();
    this.bindFlavorCards();
    this.bindPopup();
    this.bindFormSubmit();
    this.updateCounter();
    this.updateSubmitButton();
    this.updateFlavorButtonsState();
  }

  // ==========================================================================
  // Price Formatting
  // ==========================================================================

  formatPrice(cents) {
    return window.formatPrice(cents);
  }

  // ==========================================================================
  // Variant Selection
  // ==========================================================================

  initSelectedVariant() {
    const activeButton = this.container.querySelector('.pack-builder__variant--active');
    if (activeButton) {
      const variantId = parseInt(activeButton.dataset.variantId, 10);
      this.selectedVariant = this.variants.find(v => v.id === variantId);
    } else if (this.variants.length > 0) {
      this.selectedVariant = this.variants[0];
    }
  }

  bindVariantButtons() {
    this.variantButtons.forEach(button => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const variantId = parseInt(button.dataset.variantId, 10);
        this.selectVariant(variantId);
      });
    });
  }

  bindVariantsToggle() {
    if (!this.variantsToggle) return;

    this.variantsToggle.addEventListener('click', () => {
      this.toggleVariants();
    });
  }

  toggleVariants() {
    if (!this.variantsContainer) return;

    const isExpanded = this.variantsContainer.classList.toggle('pack-builder__variants--expanded');

    if (this.variantsToggleText) {
      this.variantsToggleText.textContent = isExpanded ? 'Voir moins' : 'Voir plus';
    }
  }

  expandVariants() {
    if (!this.variantsContainer || !this.variantsToggle) return;

    if (!this.variantsContainer.classList.contains('pack-builder__variants--expanded')) {
      this.variantsContainer.classList.add('pack-builder__variants--expanded');
      if (this.variantsToggleText) {
        this.variantsToggleText.textContent = 'Voir moins';
      }
    }
  }

  selectVariant(variantId) {
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) return;

    const previousVariant = this.selectedVariant;

    // If downgrading to smaller variant, reset to fit new max
    if (previousVariant && variant.qty < previousVariant.qty) {
      this.resetSelectionsToFit(variant.qty);
    }

    this.selectedVariant = variant;

    // Update active state
    this.variantButtons.forEach(btn => {
      const btnId = parseInt(btn.dataset.variantId, 10);
      btn.classList.toggle('pack-builder__variant--active', btnId === variantId);
    });

    this.updatePriceDisplay();
    this.updateCounter();
    this.updateMainFormVariant(variantId);
    this.updateSubmitButton();
    this.updateFlavorButtonsState();
  }

  updatePriceDisplay() {
    if (!this.selectedVariant) return;

    // Check if subscription is active to apply discount
    const picker = document.querySelector('[data-subscription-picker]');
    const isSubscribeActive = picker ? picker.classList.contains('is-subscribe-active') : false;
    let discountPercent = 0;

    if (isSubscribeActive) {
      const dataEl = picker.querySelector('[data-subscription-data]');
      if (dataEl) {
        try {
          const subData = JSON.parse(dataEl.textContent);
          // Get per-variant discount from allocations
          const variantData = subData.variants?.[String(this.selectedVariant.id)];
          const activeTab = picker.querySelector('.subscription-picker__tab.is-active');
          const planId = activeTab?.dataset.sellingPlanId || subData.sellingPlans?.[0]?.id;
          const alloc = variantData?.allocations?.[String(planId)];
          discountPercent = alloc?.discountPercent || subData.discountPercent || 0;
        } catch(e) {}
      }
    }

    const originalPrice = this.selectedVariant.price;
    const savings = Math.round(originalPrice * discountPercent / 100);
    const finalPrice = originalPrice - savings;

    if (this.priceDisplay) {
      this.priceDisplay.textContent = this.formatPrice(finalPrice);
    }

    if (this.comparePriceDisplay) {
      if (isSubscribeActive) {
        // Show original price as compare when subscription active
        this.comparePriceDisplay.textContent = this.formatPrice(originalPrice);
        this.comparePriceDisplay.style.display = '';
      } else {
        const hasDiscount = this.selectedVariant.compareAtPrice > originalPrice;
        this.comparePriceDisplay.textContent = this.formatPrice(this.selectedVariant.compareAtPrice);
        this.comparePriceDisplay.style.display = hasDiscount ? '' : 'none';
      }
    }
  }

  updateMainFormVariant(variantId) {
    if (!this.submitButton || !this.selectedVariant) return;

    this.submitButton.setAttribute('variant-id', variantId);
    this.submitButton.setAttribute('data-original-price', this.selectedVariant.price);
    this.submitButton.setAttribute('data-compare-price', this.selectedVariant.compareAtPrice);

    const priceEl = this.submitButton.querySelector('.product-form__price');
    const comparePriceEl = this.submitButton.querySelector('.product-form__compare-price');

    if (priceEl) {
      priceEl.textContent = this.formatPrice(this.selectedVariant.price);
    }
    if (comparePriceEl) {
      comparePriceEl.textContent = this.formatPrice(this.selectedVariant.compareAtPrice);
      const hasDiscount = this.selectedVariant.compareAtPrice > this.selectedVariant.price;
      comparePriceEl.parentElement.style.display = hasDiscount ? '' : 'none';
    }

    // Update subscription picker prices
    const productFormEl = document.querySelector('product-form');
    if (productFormEl && typeof productFormEl.updateSubscriptionPickerPrices === 'function') {
      productFormEl.updateSubscriptionPickerPrices({
        id: variantId,
        price: this.selectedVariant.price,
        compare_at_price: this.selectedVariant.compareAtPrice
      });
    }

    // Update URL
    if (window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }
  }

  // ==========================================================================
  // Submit Button State
  // ==========================================================================

  updateSubmitButton() {
    if (!this.submitButton) return;
    if (document.body.hasAttribute('data-stop-sells')) return;

    const maxQty = this.selectedVariant?.qty || 0;
    const remainingItems = maxQty - this.totalSelected;
    const remainingPacks = Math.ceil(remainingItems / this.qtyPerPack);
    const isComplete = remainingItems <= 0;

    if (isComplete) {
      // Pack is complete - show normal add to cart with price
      this.submitButton.disabled = false;
      if (this.submitButtonTextWrapper) {
        this.submitButtonTextWrapper.innerHTML = this.submitButtonOriginalTextHTML;
      }
      if (this.submitButtonSeparator) {
        this.submitButtonSeparator.style.display = '';
      }
      if (this.submitButtonPriceWrapper) {
        this.submitButtonPriceWrapper.style.display = '';
      }
    } else {
      // Pack is incomplete - show remaining packs count, hide price, and disable
      this.submitButton.disabled = true;
      const text = this.incompleteText.replace('{count}', remainingPacks);
      if (this.submitButtonTextWrapper) {
        this.submitButtonTextWrapper.innerHTML = text;
      }
      if (this.submitButtonSeparator) {
        this.submitButtonSeparator.style.display = 'none';
      }
      if (this.submitButtonPriceWrapper) {
        this.submitButtonPriceWrapper.style.display = 'none';
      }
    }
  }

  // ==========================================================================
  // Flavor Selection
  // ==========================================================================

  bindFlavorCards() {
    this.flavorCards.forEach(card => {
      const flavorId = card.dataset.flavorId;
      this.flavorSelections.set(flavorId, 0);

      card.querySelector('[data-action="add-flavor"]')?.addEventListener('click', () => {
        this.changeFlavor(flavorId, card, this.qtyPerPack);
      });

      card.querySelector('[data-action="decrease-flavor"]')?.addEventListener('click', () => {
        this.changeFlavor(flavorId, card, -this.qtyPerPack);
      });

      card.querySelector('[data-action="increase-flavor"]')?.addEventListener('click', () => {
        this.changeFlavor(flavorId, card, this.qtyPerPack);
      });
    });
  }

  changeFlavor(flavorId, card, delta) {
    const maxQty = this.selectedVariant?.qty || 0;
    const currentQty = this.flavorSelections.get(flavorId) || 0;

    // Adding flavor
    if (delta > 0) {
      const remaining = maxQty - this.totalSelected;
      if (remaining <= 0) {
        this.showUpgradePopup(flavorId, card);
        return;
      }
    }

    // Removing flavor
    if (delta < 0 && currentQty <= 0) return;

    const newQty = Math.max(0, currentQty + delta);
    this.flavorSelections.set(flavorId, newQty);
    this.totalSelected += (newQty - currentQty);

    this.updateFlavorCardUI(card, newQty);
    this.updateCounter();
    this.updateFlavorsInput();
    this.updateSubmitButton();
    this.updateFlavorButtonsState();
  }

  updateFlavorCardUI(card, qty) {
    const addBtn = card.querySelector('[data-action="add-flavor"]');
    const qtyControls = card.querySelector('[data-qty-controls]');
    const qtyDisplay = card.querySelector('[data-qty-display]');

    const hasQty = qty > 0;

    if (addBtn) addBtn.style.display = hasQty ? 'none' : '';
    if (qtyControls) qtyControls.style.display = hasQty ? 'flex' : 'none';
    if (qtyDisplay) qtyDisplay.textContent = qty;
  }

  updateFlavorButtonsState() {
    const maxQty = this.selectedVariant?.qty || 0;
    const remaining = maxQty - this.totalSelected;
    const isMaxReached = remaining <= 0;

    // Check if there's a next variant available for upgrade
    const currentIndex = this.variants.findIndex(v => v.id === this.selectedVariant?.id);
    const nextVariant = this.variants[currentIndex + 1];
    const canUpgrade = nextVariant?.available;

    // Only disable if max reached AND no upgrade possible
    const shouldDisable = isMaxReached && !canUpgrade;

    this.flavorCards.forEach(card => {
      const addBtn = card.querySelector('[data-action="add-flavor"]');
      const increaseBtn = card.querySelector('[data-action="increase-flavor"]');

      if (addBtn) {
        addBtn.disabled = shouldDisable;
      }
      if (increaseBtn) {
        increaseBtn.disabled = shouldDisable;
      }
    });
  }

  updateCounter() {
    const maxQty = this.selectedVariant?.qty || 0;

    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.totalSelected;
    }
    if (this.counterMax) {
      this.counterMax.textContent = `${maxQty} ${this.unitLabel}`;
    }
  }

  updateFlavorsInput() {
    if (!this.flavorsInput) return;

    const flavorsData = [];
    this.flavorSelections.forEach((qty, flavorId) => {
      if (qty > 0) {
        const card = this.container.querySelector(`[data-flavor-id="${flavorId}"]`);
        const title = card?.querySelector('.pack-builder__flavor-title')?.textContent.split('·')[0].trim() || flavorId;
        flavorsData.push(`${title}: x${qty}`);
      }
    });

    this.flavorsInput.value = flavorsData.join(' | ');
  }

  resetSelectionsToFit(maxQty) {
    let remaining = maxQty;

    this.flavorSelections.forEach((qty, flavorId) => {
      if (remaining <= 0) {
        this.flavorSelections.set(flavorId, 0);
      } else if (qty > remaining) {
        this.flavorSelections.set(flavorId, remaining);
        remaining = 0;
      } else {
        remaining -= qty;
      }
    });

    this.totalSelected = 0;
    this.flavorSelections.forEach(qty => {
      this.totalSelected += qty;
    });

    this.flavorCards.forEach(card => {
      const flavorId = card.dataset.flavorId;
      this.updateFlavorCardUI(card, this.flavorSelections.get(flavorId) || 0);
    });

    this.updateFlavorsInput();
  }

  resetAllSelections() {
    this.flavorSelections.forEach((_, flavorId) => {
      this.flavorSelections.set(flavorId, 0);
    });
    this.totalSelected = 0;

    this.flavorCards.forEach(card => {
      this.updateFlavorCardUI(card, 0);
    });

    this.updateCounter();
    this.updateFlavorsInput();
    this.updateSubmitButton();
    this.updateFlavorButtonsState();
  }

  // ==========================================================================
  // Upgrade Popup
  // ==========================================================================

  bindPopup() {
    if (!this.popup) return;

    this.popupCloseButtons?.forEach(btn => {
      btn.addEventListener('click', () => this.hideUpgradePopup());
    });

    this.popupConfirm?.addEventListener('click', () => this.confirmUpgrade());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.style.display !== 'none') {
        this.hideUpgradePopup();
      }
    });
  }

  showUpgradePopup(pendingFlavorId, pendingCard) {
    if (!this.popup || !this.selectedVariant) return;

    const currentIndex = this.variants.findIndex(v => v.id === this.selectedVariant.id);
    const nextVariant = this.variants[currentIndex + 1];

    if (!nextVariant?.available) return;

    this.pendingUpgrade = { flavorId: pendingFlavorId, card: pendingCard, nextVariant };

    if (this.currentVariantName) {
      this.currentVariantName.textContent = `${this.selectedVariant.qty} ${this.unitLabel}`;
    }
    if (this.nextVariantName) {
      this.nextVariantName.textContent = `${nextVariant.qty} ${this.unitLabel}`;
    }

    this.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  hideUpgradePopup() {
    if (!this.popup) return;

    this.popup.style.display = 'none';
    document.body.style.overflow = '';
    this.pendingUpgrade = null;
  }

  confirmUpgrade() {
    if (!this.pendingUpgrade) {
      this.hideUpgradePopup();
      return;
    }

    const { flavorId, card, nextVariant } = this.pendingUpgrade;

    // Expand variants if the next variant is hidden (index > 4)
    const nextVariantButton = this.container.querySelector(`[data-variant-id="${nextVariant.id}"]`);
    if (nextVariantButton?.classList.contains('pack-builder__variant--hidden')) {
      this.expandVariants();
    }

    this.selectVariant(nextVariant.id);

    // Add the flavor that triggered the upgrade
    const currentQty = this.flavorSelections.get(flavorId) || 0;
    const newQty = currentQty + this.qtyPerPack;
    this.flavorSelections.set(flavorId, newQty);
    this.totalSelected += this.qtyPerPack;

    this.updateFlavorCardUI(card, newQty);
    this.updateCounter();
    this.updateFlavorsInput();
    this.updateSubmitButton();
    this.updateFlavorButtonsState();
    this.hideUpgradePopup();
  }

  // ==========================================================================
  // Form Submit
  // ==========================================================================

  bindFormSubmit() {
    if (!this.submitButton) return;

    const newButton = this.submitButton.cloneNode(true);
    this.submitButton.parentNode.replaceChild(newButton, this.submitButton);
    this.submitButton = newButton;

    // Re-cache child elements after cloning
    this.submitButtonTextWrapper = this.submitButton.querySelector('.fw-6:not([variant-price-change])');
    this.submitButtonSeparator = this.submitButton.querySelector('.submit-separator');
    this.submitButtonPriceWrapper = this.submitButton.querySelector('[variant-price-change]');

    this.submitButton.addEventListener('click', async (e) => {
      e.preventDefault();

      // Only proceed if pack is complete
      if (this.totalSelected < this.selectedVariant?.qty) {
        return;
      }

      await this.handleAddToCart(this.submitButton);
    });
  }

  async handleAddToCart(button) {
    if (!this.selectedVariant) return;

    // Build line item properties
    const contentParts = [];
    const idParts = [];

    this.flavorSelections.forEach((qty, flavorId) => {
      const card = this.container.querySelector(`[data-flavor-id="${flavorId}"]`);
      const variantId = card?.dataset.flavorVariantId || flavorId;
      const title = card?.querySelector('.pack-builder__flavor-title')?.textContent.split('·')[0].trim() || 'Flavor';
      const packs = qty / this.qtyPerPack;

      if (qty > 0) {
        contentParts.push(`${qty} x ${title}`);
      }

      idParts.push(`${variantId}:${packs}`);
    });

    const properties = {
      'Contenu': contentParts.join(', ')
    };

    // UI loading state
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (this.submitButtonTextWrapper) {
      this.submitButtonTextWrapper.innerHTML = 'AJOUT...';
    }
    if (this.submitButtonSeparator) {
      this.submitButtonSeparator.style.display = 'none';
    }
    if (this.submitButtonPriceWrapper) {
      this.submitButtonPriceWrapper.style.display = 'none';
    }

    try {
      const sellingPlanInput = document.querySelector('[data-selling-plan-input]');
      const sellingPlanId = sellingPlanInput ? sellingPlanInput.value : '';
      const productFormEl = this.container.closest('product-form');
      const activePlanName = productFormEl?.getActiveSellingPlanName?.() || '';

      // Build pack builder items list
      const packBuilderItems = [];
      this.flavorSelections.forEach((qty, flavorId) => {
        if (qty > 0) {
          const card = this.container.querySelector(`[data-flavor-id="${flavorId}"]`);
          packBuilderItems.push({
            id: card?.dataset.flavorVariantId || flavorId,
            productId: card?.dataset.flavorProductId || '',
            collectionId: card?.dataset.flavorCollectionId || '',
            quantity: qty / this.qtyPerPack,
            name: card?.querySelector('.pack-builder__flavor-title')?.textContent.split('·')[0].trim() || ''
          });
        }
      });

      let response;

      // Check if product has selling plans (subscription picker present or selling plan active)
      const subscriptionPickerExists = !!document.querySelector('[data-subscription-picker]');
      const needsRechargeBundle = packBuilderItems.length > 0 && (sellingPlanId || subscriptionPickerExists);

      if (needsRechargeBundle && typeof recharge !== 'undefined' && recharge.bundle) {
        response = await this.submitRechargeBundle(packBuilderItems, sellingPlanId, activePlanName, properties);
      } else {
        if (needsRechargeBundle) {
          console.warn('[Recharge PackBuilder] Fallback sans _rb_id — recharge.bundle non disponible');
        }
        const cartBody = {
          id: this.selectedVariant.id,
          quantity: 1,
          properties
        };
        if (sellingPlanId) {
          cartBody.selling_plan = parseInt(sellingPlanId);
        }
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(cartBody),
          credentials: 'same-origin',
        });
      }

      if (!response.ok) throw new Error('Failed to add to cart');

      if (this.submitButtonTextWrapper) {
        this.submitButtonTextWrapper.innerHTML = 'AJOUTÉ !';
      }

      document.dispatchEvent(new CustomEvent('product:added'));

      const sideCart = document.querySelector('side-cart');
      if (sideCart) sideCart.classList.add('open');

      this.resetAllSelections();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.updateSubmitButton();
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setTimeout(() => {
        button.removeAttribute('aria-busy');
        this.updateSubmitButton();
      }, 1500);
    }
  }

  async submitRechargeBundle(packBuilderItems, sellingPlanId, activePlanName, existingProperties) {
    const productFormEl = this.container.closest('product-form');
    const bundleProductId = productFormEl?.dataset.productId || '';
    const bundleVariantId = String(this.selectedVariant.id);

    // Get the bundle product's first selling plan as fallback
    const subscriptionData = productFormEl?.subscriptionData;
    const fallbackSellingPlanId = subscriptionData?.sellingPlans?.[0]?.id
      ? String(subscriptionData.sellingPlans[0].id)
      : sellingPlanId;

    // Build selections — all IDs must be strings for Recharge SDK
    const selections = packBuilderItems.map(item => {
      const card = this.container.querySelector(`[data-flavor-variant-id="${item.id}"]`) ||
                   this.container.querySelector(`[data-flavor-id="${item.productId}"]`);

      // Use active selling plan, or flavor's matching plan, or bundle's first plan
      let itemSellingPlanId = '';
      if (sellingPlanId) {
        itemSellingPlanId = ProductTemplate.findMatchingSellingPlanId(
          card, activePlanName, 'data-flavor-selling-plans'
        ) || sellingPlanId;
      } else {
        // One-time: try flavor's first plan, then fallback to bundle's first plan
        try {
          const plans = JSON.parse(card?.getAttribute('data-flavor-selling-plans') || '[]');
          if (plans.length > 0) itemSellingPlanId = String(plans[0].id);
        } catch(e) {}
        if (!itemSellingPlanId) itemSellingPlanId = fallbackSellingPlanId;
      }

      return {
        externalProductId: String(item.productId),
        externalVariantId: String(item.id),
        collectionId: String(item.collectionId),
        quantity: item.quantity,
        sellingPlan: String(itemSellingPlanId)
      };
    });

    const bundle = {
      externalProductId: String(bundleProductId),
      externalVariantId: String(bundleVariantId),
      selections: selections
    };

    console.log('[Recharge PackBuilder] bundle:', JSON.stringify(bundle, null, 2));

    let rbId = '';
    try {
      const isValid = await recharge.bundle.validateBundle(bundle);
      console.log('[Recharge PackBuilder] validateBundle:', isValid);

      const { valid, error } = await recharge.bundle.validateBundleSelection(bundle);
      console.log('[Recharge PackBuilder] validateBundleSelection:', valid, error);

      if (isValid && valid) {
        rbId = await recharge.bundle.getBundleId(bundle);
        console.log('[Recharge PackBuilder] _rb_id:', rbId);
      } else {
        console.error('[Recharge PackBuilder] validation failed — validateBundle:', isValid, '— validateBundleSelection:', error, bundle);
      }
    } catch(e) {
      console.error('[Recharge PackBuilder] Erreur getBundleId:', e);
    }

    // Build flavor plans mapping for sidecart plan switching
    const flavorPlansMap = {};
    selections.forEach(sel => {
      const card = this.container.querySelector(`[data-flavor-variant-id="${sel.externalVariantId}"]`) ||
                   this.container.querySelector(`[data-flavor-id="${sel.externalProductId}"]`);
      try {
        const plans = JSON.parse(card?.getAttribute('data-flavor-selling-plans') || '[]');
        if (plans.length > 0) flavorPlansMap[sel.externalVariantId] = plans;
      } catch(e) {}
    });

    // Add to cart with Recharge bundle properties
    const cartItem = {
      id: parseInt(bundleVariantId),
      quantity: 1,
      properties: {
        ...existingProperties
      }
    };
    if (rbId) {
      cartItem.properties._rb_id = rbId;
      cartItem.properties._rb_selections = JSON.stringify(bundle);
    }
    if (Object.keys(flavorPlansMap).length > 0) {
      cartItem.properties._flavor_plans = JSON.stringify(flavorPlansMap);
    }
    if (sellingPlanId) {
      cartItem.selling_plan = parseInt(sellingPlanId);
    }

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [cartItem] })
    });
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PackBuilder());
} else {
  new PackBuilder();
}
